/**
 * [Frontend Sprint 2026-07-10 §Part 4] DiamondSharePage - public OG
 * share card for the daily Diamond in the Rough pick.
 *
 * URL: /diamond/{date}  (date is informational; endpoint is
 * deterministic-per-day off the current scored cohort until we backfill
 * the historical archive).
 *
 * No auth. Meta tags injected into <head> at render time so LinkedIn /
 * X / Discord unfurl the pick with photo + name + ADP→PGF delta. Every
 * share becomes a landing-page hook back to the paid product.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Gem, User, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function setMeta(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function DiamondSharePage() {
  const { date } = useParams();
  const [pick, setPick] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setGated(false);
    axios
      .get(`${API}/analytics/edge/diamond/public/${date || "today"}`)
      .then((r) => !cancelled && setPick(r.data?.diamond || null))
      .catch((err) => {
        if (cancelled) return;
        const s = err?.response?.status;
        if (s === 401 || s === 403) setGated(true);
        setPick(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [date]);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.href;
  }, []);

  useEffect(() => {
    if (!pick) {
      document.title = "Diamond in the Rough - Pregame Fantasy";
      return;
    }
    const title = `${pick.name} - Diamond in the Rough | Pregame Fantasy`;
    const description =
      `Market has him at #${pick.adp_rank_overall}. ` +
      `PGF has him at #${pick.pgf_rank_overall}.` +
      (pick.driving_axis ? ` Edge driver: ${pick.driving_axis}.` : "");
    const image = pick.photo_url ||
      "https://sleepercdn.com/images/v2/icons/player_default.webp";
    document.title = title;
    setMeta("description", description);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:image", image, true);
    setMeta("og:type", "article", true);
    setMeta("og:url", shareUrl, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);
  }, [pick, shareUrl]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 py-10">
      <div
        className="relative w-full max-w-xl rounded-2xl border border-white/10 bg-[#111111] p-6 sm:p-8 shadow-2xl overflow-hidden"
        data-testid="diamond-share-card"
      >
        {/* Ambient orange corner glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full"
          style={{
            background: "radial-gradient(closest-side, #FF6B0033, transparent)",
          }}
        />

        <div className="flex items-center gap-2 mb-4">
          <Gem className="w-5 h-5" style={{ color: "#FF6B00" }} />
          <div>
            <p
              className="font-display font-black text-xs uppercase tracking-widest"
              style={{ color: "#FF6B00" }}
            >
              Diamond in the Rough
            </p>
            <p className="font-mono text-[10px] text-white/60">
              Pregame Fantasy · Daily deep Find past ADP #100
            </p>
          </div>
        </div>

        {loading && (
          <div className="py-16 text-center font-mono text-sm text-white/50" data-testid="diamond-share-loading">
            Loading today's pick...
          </div>
        )}

        {!loading && gated && (
          <div className="py-14 text-center" data-testid="diamond-share-gated">
            <p className="font-display font-black text-lg uppercase tracking-widest" style={{ color: "#FF6B00" }}>
              Internal Review
            </p>
            <p className="mt-2 font-mono text-sm text-white/70">
              The Diamond is under founder review while the composite trust check runs.
            </p>
            <p className="mt-1 font-mono text-xs text-white/50">
              Available on next release.
            </p>
          </div>
        )}

        {!loading && !gated && !pick && (
          <div className="py-16 text-center font-mono text-sm text-white/50" data-testid="diamond-share-empty">
            No qualifying pick published for this date.
          </div>
        )}

        {!loading && !gated && pick && (
          <>
            <div className="flex items-center gap-5">
              {imgErr || !pick.photo_url ? (
                <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center shrink-0" data-testid="diamond-share-photo-fallback">
                  <User className="w-8 h-8 text-white/40" />
                </div>
              ) : (
                <img
                  src={pick.photo_url}
                  alt={pick.name}
                  onError={() => setImgErr(true)}
                  className="w-20 h-20 rounded-full object-cover bg-white/5 shrink-0"
                  data-testid="diamond-share-photo"
                />
              )}
              <div className="min-w-0 flex-1">
                <div
                  className="font-display font-black text-2xl sm:text-3xl truncate"
                  data-testid="diamond-share-name"
                >
                  {pick.name}
                </div>
                <div className="font-mono text-xs text-white/70 mt-1">
                  {pick.position} · {pick.nfl_team || "FA"}
                </div>
              </div>
            </div>

            <div
              className="mt-5 flex items-center gap-3 rounded-xl border border-white/10 bg-black/40 px-4 py-3"
              data-testid="diamond-share-delta"
            >
              <div className="flex-1">
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/50">
                  Market ADP
                </p>
                <p
                  className="font-display font-bold text-2xl"
                  style={{ color: "#FF6B00" }}
                  data-testid="diamond-share-adp"
                >
                  #{pick.adp_rank_overall}
                </p>
              </div>
              <ArrowRight className="w-6 h-6 text-white/40" />
              <div className="flex-1 text-right">
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/50">
                  PGF Rank
                </p>
                <p
                  className="font-display font-bold text-2xl"
                  style={{ color: "#FF6B00" }}
                  data-testid="diamond-share-pgf"
                >
                  #{pick.pgf_rank_overall}
                </p>
              </div>
            </div>

            <p
              className="mt-4 text-xs sm:text-sm uppercase tracking-widest text-white/70 font-mono"
              data-testid="diamond-share-driver"
            >
              {pick.driving_axis ? (
                <>Edge driver: <span className="lowercase text-white">{pick.driving_axis}</span></>
              ) : (
                <span className="opacity-60">Edge driver: n/a</span>
              )}
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to="/login"
                data-testid="diamond-share-cta-primary"
                className="flex-1 text-center rounded-full px-5 py-2.5 font-display font-bold text-xs uppercase tracking-wider text-black"
                style={{ background: "#FF6B00" }}
              >
                Get the full analysis
              </Link>
              <Link
                to="/"
                data-testid="diamond-share-cta-secondary"
                className="flex-1 text-center rounded-full px-5 py-2.5 font-display font-bold text-xs uppercase tracking-wider text-white/70 border border-white/20 hover:bg-white/[0.05] transition-colors"
              >
                About Pregame Fantasy
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
